import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  InputAdornment,
  Alert,
  Snackbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import axios from "axios";
import SelectReact from "react-select";

import apiUrl from "../api";

const AddPoolActionForm = ({ setShowAddPoolActionForm, editMode = false, poolActionData = null }) => {
  const [properties, setProperties] = useState([]);
  const [poolOptions, setPoolOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Simplified state for repetitive actions
  const [isRepetitive, setIsRepetitive] = useState(false);
  const [customDays, setCustomDays] = useState(1); // Days between repetitions
  const [endDate, setEndDate] = useState('');

  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [futureActionsCount, setFutureActionsCount] = useState(0);
  const [isUpdatingRecurring, setIsUpdatingRecurring] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const [formData, setFormData] = useState({
    property: "",
    date: new Date().toISOString().split('T')[0], // Format as YYYY-MM-DD
    time: "",
    pool_options: [],
    done: false,
    time_done: null,
    charge: 0
  });

  // Initialize form data when in edit mode
  useEffect(() => {
    if (editMode && poolActionData) {
      const date = new Date(poolActionData.date).toISOString().split('T')[0];
      setFormData({
        property: poolActionData.property,
        date: date,
        time: poolActionData.time || "",
        pool_options: poolActionData.pool_options || [],
        done: poolActionData.done || false,
        time_done: poolActionData.time_done || null,
        charge: poolActionData.charge || 0
      });
    }
  }, [editMode, poolActionData]);

  // Fetch properties and pool options when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [propertiesRes, optionsRes] = await Promise.all([
          axios.get(`${apiUrl}/property/getPoolProperties`,
            {
              headers: {
                Authorization: `Bearer ${user.token}`,
              },
            }
          ),
          axios.get(`${apiUrl}/pool-options/getPoolOptions`,
            {
              headers: {
                Authorization: `Bearer ${user.token}`,
              },
            }
          )
        ]);
        
        // Format properties for react-select
        const receivedProperties = propertiesRes.data.properties || [];
        const formattedProperties = receivedProperties.map(prop => ({
          value: prop._id,
          label: prop.name
        }));
        
        setProperties(formattedProperties);
        
        const options = optionsRes.data || [];
        setPoolOptions(options);
        
        // If not in edit mode, initialize all pool options
        if (!editMode) {
          const initialPoolOptions = options.map(option => ({
            _id: option._id,
            price: option.price,
            value: null,
            is_number: false
          }));
          
          setFormData(prevFormData => ({
            ...prevFormData,
            pool_options: initialPoolOptions
          }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [editMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle react-select property change
  const handlePropertyChange = (selectedOption) => {
    setFormData({ ...formData, property: selectedOption.value });
  };

  // Add a function to handle price changes
  const handlePriceChange = (optionId, newPrice) => {
    const currentOptions = [...formData.pool_options];
    const optionIndex = currentOptions.findIndex(opt => opt._id === optionId);
    
    if (optionIndex >= 0) {
      currentOptions[optionIndex].price = Number(newPrice);
      setFormData({ ...formData, pool_options: currentOptions });
    }
  };

  const handleOptionValueChange = (optionId, value, is_number = false) => {
    const currentOptions = [...formData.pool_options];
    const optionIndex = currentOptions.findIndex(opt => opt._id === optionId);
    
    if (optionIndex >= 0) {
      // Convert comma decimal separators to dots for parsing
      const processedValue = value.toString().replace(',', '.');
      currentOptions[optionIndex].value = is_number ? Number(processedValue) : processedValue;
      currentOptions[optionIndex].is_number = is_number;
      setFormData({ ...formData, pool_options: currentOptions });
    }
  };

  // Close the success alert
  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSuccessAlert(false);
  };

  // Function to calculate dates for repetitive actions
  const getRepetitiveDates = () => {
    // Only applicable for new actions, not for editing
    if (!isRepetitive || !endDate || editMode) return [new Date(formData.date)];

    const startDate = new Date(formData.date);
    const end = new Date(endDate);
    const dates = [new Date(startDate)];
    
    let currentDate = new Date(startDate);
    
    while (true) {
      // Add the specified number of days to get the next date
      const nextDate = new Date(currentDate);
      nextDate.setDate(currentDate.getDate() + Number(customDays));
      
      if (nextDate > end) break;
      
      dates.push(nextDate);
      currentDate = nextDate;
    }
    
    return dates;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property) {
      alert("Please select a property");
      return;
    }

    // For repetitive actions, validate end date
    if (isRepetitive && !endDate && !editMode) {
      alert("Please select an end date for repetitive actions");
      return;
    }

    setIsSubmitting(true);
    try {
      if (editMode) {
        // Update existing pool action
        const submissionData = { 
          ...formData,
          date: new Date(formData.date).toISOString()
        };

        await axios.put(
          `${apiUrl}/pool-action/${poolActionData._id}`, 
          submissionData,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        
        setSuccessMessage("Pool action updated successfully!");
        setShowSuccessAlert(true);
        
        // Close the form after 2 seconds
        setTimeout(() => {
          setShowAddPoolActionForm && setShowAddPoolActionForm(false);
        }, 2000);
      } else {
        // Create new pool action(s)
        const dates = getRepetitiveDates();
        const totalActions = dates.length;
        let successCount = 0;

        // Create a pool action for each date
        for (let i = 0; i < dates.length; i++) {
          const date = dates[i];
          const submissionData = { 
            ...formData,
            date: date.toISOString(),
            // Include charge only for the first action
            charge: i === 0 ? formData.charge : 0
          };

          await axios.post(
            `${apiUrl}/pool-action`, 
            submissionData,
            {
              headers: {
                Authorization: `Bearer ${user.token}`,
              },
            }
          );
          successCount++;
        }
        
        // Show success message
        const actionText = totalActions > 1 
          ? `${successCount}/${totalActions} pool actions created successfully!` 
          : "Pool action created successfully!";
        
        setSuccessMessage(actionText);
      }
      
      setShowSuccessAlert(true);
      
      // Close the form after 2 seconds
      setTimeout(() => {
        setShowAddPoolActionForm && setShowAddPoolActionForm(false);
      }, 2000);
      
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(`Failed to ${editMode ? 'update' : 'create'} pool action: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkAsDone = async () => {
    try {
      setIsSubmitting(true);
      
      await axios.patch(
        `${apiUrl}/pool-action/${poolActionData._id}/done`,
        { time_done: new Date() },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      
      setSuccessMessage("Pool action completed successfully!");
      setShowSuccessAlert(true);
      
      // Close the form after 2 seconds
      setTimeout(() => {
        setShowAddPoolActionForm && setShowAddPoolActionForm(false);
      }, 2000);
      
    } catch (error) {
      console.error("Error marking action as done:", error);
      alert(`Failed to complete pool action: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRecurring = async (updateAll) => {
    setIsUpdatingRecurring(true);
    try {
      const response = await axios.post(
        `${apiUrl}/pool-action/update-recurring`,
        {
          originalActionId: poolActionData._id,
          property: formData.property,
          newDate: new Date(formData.date).toISOString(),
          newTime: formData.time,
          updateAll
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      
      setSuccessMessage(`Pool action${response.data.updatedCount > 0 ? 's' : ''} updated successfully!`);
      setShowSuccessAlert(true);
      
      // Close the form after 2 seconds
      setTimeout(() => {
        setShowAddPoolActionForm && setShowAddPoolActionForm(false);
      }, 2000);
    } catch (error) {
      console.error("Error updating recurring actions:", error);
      alert(`Failed to update recurring actions: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsUpdatingRecurring(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Custom styles for React Select
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '56px',
      marginBottom: '16px',
      marginTop: '8px'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };

  // Find the current property in the properties array for the select component
  const selectedPropertyOption = properties.find(prop => prop.value === formData.property) || null;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: "100%", maxWidth: "600px" }}>
      {/* Success Alert */}
      <Snackbar 
        open={showSuccessAlert} 
        autoHideDuration={2000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Typography variant="h6" component="h2" gutterBottom>
        {editMode ? 'Edit Pool Action' : 'New Pool Action'}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>Select Property</Typography>
      <SelectReact
        options={properties}
        onChange={handlePropertyChange}
        value={selectedPropertyOption}
        placeholder="Select property..."
        noOptionsMessage={() => "No properties found"}
        styles={selectStyles}
        isSearchable
        isDisabled={editMode} // Disable property change when editing
      />

      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        <TextField
          label="Date"
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
        />
        <TextField
          label="Time (optional)"
          type="time"
          name="time"
          value={formData.time || ""}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </Box>

      {/* Repetitive actions section - only show for new actions, not for editing */}
      {!editMode && (
        <Box sx={{ mt: 2 }}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Typography>Recurring Action</Typography>
            </Grid>
            <Grid item>
              <TextField
                label="Every"
                type="number"
                size="small"
                value={customDays}
                onChange={(e) => setCustomDays(Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{
                  inputProps: { min: 1 },
                  endAdornment: <InputAdornment position="end">days</InputAdornment>,
                }}
                disabled={!isRepetitive}
                sx={{ width: 120, ml: 2 }}
              />
            </Grid>
            <Grid item>
              <TextField
                label="Until"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={!isRepetitive}
                sx={{ ml: 2 }}
              />
            </Grid>
            <Grid item>
              <Button
                variant={isRepetitive ? "contained" : "outlined"}
                size="small"
                onClick={() => setIsRepetitive(!isRepetitive)}
                sx={{ ml: 1 }}
              >
                {isRepetitive ? "Active" : "Inactive"}
              </Button>
            </Grid>
          </Grid>
          
          {isRepetitive && endDate && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {getRepetitiveDates().length} actions will be created
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />
{
  !editMode && (
  <>
      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
        Pool Options
      </Typography>

      <TextField
        label="Charge"
        type="number"
        name="charge"
        value={formData.charge}
        onChange={handleChange}
        InputProps={{
          endAdornment: <InputAdornment position="end">€</InputAdornment>,
        }}
        fullWidth
        sx={{ mb: 2 }}
      />

      <Box sx={{ mt: 2 }}>
        {formData.pool_options.map((poolOption, index) => {
          const option = poolOptions.find(opt => opt._id === poolOption._id);
          if (!option) return null;
          
          return (
            <Box key={poolOption._id} sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="subtitle2">{option.title}</Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Price"
                    size="small"
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={poolOption.price || ""}
                    onChange={(e) => handlePriceChange(poolOption._id, e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Value"
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={poolOption.value || ""}
                    onChange={(e) => handleOptionValueChange(poolOption._id, e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Box>
      </>
      )}

    

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setShowAddPoolActionForm && setShowAddPoolActionForm(false)}
        >
          Cancel
        </Button>
        
        {editMode ? (
          <>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !formData.property}
              onClick={(e) => {
                e.preventDefault();
                handleUpdateRecurring(false);
              }}
            >
              {isSubmitting ? 
                <CircularProgress size={24} /> : 
                "Save Current Action"
              }
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={isSubmitting || !formData.property}
              onClick={(e) => {
                e.preventDefault();
                handleUpdateRecurring(true);
              }}
            >
              {isSubmitting ? 
                <CircularProgress size={24} /> : 
                "Save All Future Actions"
              }
            </Button>
          </>
        ) : (
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !formData.property || (isRepetitive && !endDate) || (isRepetitive && customDays < 1)}
          >
            {isSubmitting ? 
              <CircularProgress size={24} /> : 
              isRepetitive ? "Create Recurring Actions" : "Create"
            }
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AddPoolActionForm;
